@let property = property$ | async;

<red-header-content>
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-back-button [defaultHref]="defaultHref"></ion-back-button>
    </ion-buttons>
    <ion-title>Property Details</ion-title>
    @if (selectFarmAlertsEnabled$ | async) {
      <ion-buttons slot="primary">
        <ion-button class="tw:relative tw:overflow-visible" [routerLink]="['alerts']">
          <ion-icon slot="icon-only" name="notifications-outline"> </ion-icon>
          @if (property?.unreadFarmAlerts) {
            <ion-badge class="tw:absolute tw:-top-0.5 tw:-right-1" color="danger">
              {{ property?.unreadFarmAlerts }}
            </ion-badge>
          }
        </ion-button>
      </ion-buttons>
    }
  </ion-toolbar>
</red-header-content>
<red-page-content>
  <ion-card>
    <ion-card-content>
      <div class="tw:flex tw:justify-between tw:align-middle">
        <div>
          <div class="tw:pt-2 tw:text-sm"><ion-text color="medium">Address:</ion-text></div>
          <div class="tw:text-lg tw:font-bold">{{ property | redAddress: addressType.LineOne }}</div>
          <div class="tw:text-lg tw:font-bold">{{ property | redAddress: addressType.LineTwo }}</div>
          <div class="tw:pt-2 tw:text-sm">Parcel Number:</div>
          <div class="tw:text-lg tw:font-bold">{{ property?.A037_ParcelNumber | redDashIfEmpty }}</div>
        </div>
      </div>

      <red-walking-farm-property-lead-status-selector
        class="tw:block tw:w-full"
        [property]="property"
        [walkingFarmId]="walkingFarmId"
      >
      </red-walking-farm-property-lead-status-selector>
    </ion-card-content>
  </ion-card>

  <form [formGroup]="propertyForm">
    <ion-list [inset]="true" lines="full" class="no-last-line">
      <ion-item-group>
        <ion-item-divider color="primary">
          <ion-label>Owner Information</ion-label>
        </ion-item-divider>
        <ion-item>
          <ion-label color="medium tw:text-wrap tw:text-sm">
            {{ property?.A017_FullMailAddress | redDashIfEmpty }}<br />{{
              property?.A018_MailAddressCityState | redDashIfEmpty
            }}
            {{ property?.A019_MailAddressZip4 }}
          </ion-label>
        </ion-item>
        <ion-item>
          <ion-input
            labelPlacement="stacked"
            placeholder="Primary Owner Full Name"
            inputmode="text"
            type="text"
            formControlName="primaryOwnerName"
            ><div slot="label">1st Owner:</div></ion-input
          >
        </ion-item>
        <ion-item>
          <ion-input
            labelPlacement="stacked"
            placeholder="Secondary Owner Full Name"
            inputmode="text"
            type="text"
            formControlName="secondaryOwnerName"
            ><div slot="label">2nd Owner:</div></ion-input
          >
        </ion-item>
        <ion-item>
          <ion-input
            labelPlacement="stacked"
            placeholder="555-555-5555"
            inputmode="tel"
            [required]="true"
            type="text"
            formControlName="phone"
            [maskito]="phoneMask"
            [maskitoElement]="maskPredicate"
            ><div slot="label">Phone:</div></ion-input
          >
        </ion-item>
        <ion-item>
          <ion-input
            redErrorMessage
            labelPlacement="stacked"
            placeholder="email@example.com"
            inputmode="email"
            type="email"
            formControlName="email"
            ><div slot="label">Email:</div></ion-input
          >
        </ion-item>
        <ion-item>
          <ion-textarea
            redTextAreaLogs
            [autoGrow]="true"
            labelPlacement="stacked"
            placeholder="Type here some notes..."
            inputmode="text"
            type="text"
            formControlName="notes"
          >
            <div slot="label">Notes:</div>
          </ion-textarea>
        </ion-item>
      </ion-item-group>
    </ion-list>

    @if (property?.contactDetails?.length) {
      <ion-list [inset]="true" lines="full" class="no-last-line">
        <ion-list-header>
          <ion-label color="success">Purchased Phone & Emails</ion-label>
        </ion-list-header>
        @for (contactDetail of property?.contactDetails; track contactDetail) {
          <ion-item-group>
            <ion-item-divider color="success">
              <ion-label>{{ contactDetail.firstName }} {{ contactDetail.lastName }}</ion-label>
            </ion-item-divider>
            <ion-item class="tw:text-sm tw:italic">
              <div slot="end">
                Updated on
                <span class="tw:mx-1 tw:font-bold">{{ contactDetail.createdAt | date: 'MM/dd/yyyy' }}</span> at
                <span class="tw:mx-1 tw:font-bold">{{ contactDetail.createdAt | date: 'h:mm a' }}</span>
              </div>
            </ion-item>
            @for (phoneNumber of contactDetail.phoneNumbers; track phoneNumber) {
              <ion-item [button]="true" href="tel:{{ phoneNumber }}">
                <ion-icon slot="start" name="call-outline" color="success"></ion-icon>
                {{ phoneNumber }}
              </ion-item>
            }
            @for (email of contactDetail.emails; track email) {
              <ion-item [button]="true" (click)="composeEmail(email)">
                <ion-icon slot="start" name="mail-outline" color="success"></ion-icon>
                {{ email }}
              </ion-item>
            }
          </ion-item-group>
        }
      </ion-list>
    }

    <ion-list [inset]="true" lines="full" class="no-last-line">
      <ion-item-group>
        <ion-item-divider color="primary">
          <ion-label>Property Information</ion-label>
        </ion-item-divider>
        <ion-item>
          <ion-input
            redErrorMessage
            labelPlacement="stacked"
            placeholder="0"
            inputmode="numeric"
            type="text"
            [maskito]="surfaceMask"
            [maskitoElement]="maskPredicate"
            formControlName="propertySize"
          >
            <div slot="label">Property Size (Sq Ft):</div>
          </ion-input>
        </ion-item>
        <ion-item>
          <ion-input
            redErrorMessage
            labelPlacement="stacked"
            placeholder="0"
            inputmode="numeric"
            type="text"
            [maskito]="surfaceMask"
            [maskitoElement]="maskPredicate"
            formControlName="lotSize"
          >
            <div slot="label">Lot Size ({{ property?.A036_LotAreaUnits || 'Sq Ft' }}):</div>
          </ion-input>
        </ion-item>
      </ion-item-group>
      <ion-item-divider>Bedrooms</ion-item-divider>
      <red-numeric-buttons-input ngDefaultControl formControlName="bedrooms"></red-numeric-buttons-input>
      <ion-item-divider>Bathrooms</ion-item-divider>
      <red-numeric-buttons-input ngDefaultControl formControlName="bathrooms"></red-numeric-buttons-input>
      <ion-item-divider>Pool</ion-item-divider>
      <ion-radio-group formControlName="pool">
        <div class="tw:flex tw:justify-around tw:p-2">
          <div>
            <ion-radio [value]="'P'" color="success">Yes</ion-radio>
          </div>
          <div>
            <ion-radio [value]="'N'" color="danger">No</ion-radio>
          </div>
        </div>
      </ion-radio-group>
    </ion-list>
  </form>
  <ion-card>
    <ion-card-content>
      <ion-grid class="ion-no-padding">
        <ion-row>
          <ion-col size="6" class="tw:border-r-solid tw:border-r tw:border-gray-200">
            <div class="tw:pt-2 tw:text-sm">Sale Price:</div>
            <div class="tw:text-lg tw:font-bold">{{ property?.A029_SalesPrice | redCurrency }}</div>
            <div class="tw:pt-2 tw:text-sm">Sale Date:</div>
            <div class="tw:text-lg tw:font-bold">
              {{ property?.A030_SaleDate | date: 'MM/dd/yyyy' | redDashIfEmpty }}
            </div>
          </ion-col>
          <ion-col size="6" class="tw:flex tw:flex-col tw:items-end">
            <div class="tw:pt-2 tw:text-sm">Tax Value:</div>
            <div class="tw:text-lg tw:font-bold">
              {{ property?.A032_AssessedLandValue | redCurrency }}
            </div>
            <div class="tw:pt-2 tw:text-sm">Year Built:</div>
            <div class="tw:text-lg tw:font-bold">
              {{ property?.A038_YearBuilt | redDashIfEmpty }}
            </div>
          </ion-col>
        </ion-row>
      </ion-grid>
    </ion-card-content>
  </ion-card>
  <div class="tw:m-4">
    <ion-button expand="block" (click)="loadProfile()">
      <ion-icon slot="end" name="chevron-forward-circle-outline"></ion-icon>Property Profile
    </ion-button>
  </div>
  <ion-grid class="tw:m-4">
    <ion-row>
      <ion-col size="6" (click)="exportLead()">
        <div class="tw:flex tw:flex-col tw:items-center">
          <ion-button shape="round" fill="solid" size="large">
            <ion-icon slot="icon-only" name="share-outline"></ion-icon>
          </ion-button>
          <ion-text color="primary">Export Lead</ion-text>
        </div>
      </ion-col>
      <ion-col size="6" (click)="getDirections()">
        <div class="tw:flex tw:flex-col tw:items-center">
          <ion-button shape="round" fill="solid" size="large">
            <ion-icon slot="icon-only" src="/assets/svg/directions.svg"></ion-icon>
          </ion-button>
          <ion-text color="primary">Directions</ion-text>
        </div>
      </ion-col>
    </ion-row>
  </ion-grid>
</red-page-content>
